import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ClipboardJS from 'clipboard';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import styled from 'styled-components';
import dailyMailSrc from 'assets/Home/daily_mail_logo.svg';
import appstoreSrc from 'assets/Home/home_appstore.png';
import googleplaySrc from 'assets/Home/home_googleplay.png';
import mensJournalSrc from 'assets/Home/mens_journal_logo.svg';
import telegraphSrc from 'assets/Home/telegraph_logo.svg';
import analytics from 'utils/analytics';
import { generateAdjustInviteQuery } from 'utils/adjust';
import { desktop } from '../utils/mediaQueries';
const messages = defineMessages({
    title: 'Trusted by the best',
    subtitle: 'NHS, Apple, Amazon, and hundreds of businesses rely on Sweatcoin to make millions healthier',
    telegraph: '“If ever you’ve needed an incentive to exercise, a new app could give you a much-needed boost to get active”',
    dailyMail: '“Sweatcoin.. beats Whatsapp as the most popular in App Store.”',
    mensJournal: '“Basically pays you to move and exercise, and it costs nothing to use”',
    downloadCta: 'Download Sweatcoin for free',
    appstoreName: 'Apple App Store',
    appstoreRating: '(273k ratings)',
    googleplayName: 'Google play',
    googleplayRating: '(1.8M ratings)',
});
const slides = [
    {
        logo: telegraphSrc,
        text: _jsx(FormattedMessage, Object.assign({}, messages.telegraph)),
        alt: 'Telegraph logo',
    },
    {
        logo: dailyMailSrc,
        text: _jsx(FormattedMessage, Object.assign({}, messages.dailyMail)),
        alt: 'DailyMail logo',
    },
    {
        logo: mensJournalSrc,
        text: _jsx(FormattedMessage, Object.assign({}, messages.mensJournal)),
        alt: 'Mens Journal logo',
    },
];
const configSelector = (state) => state.config;
const Quotes = () => {
    const appstoreButton = useRef();
    const googleplayButton = useRef();
    // Config & links
    const config = useSelector(configSelector);
    const { origin } = config;
    // Rebuild on copy so the Facebook params are read at click time, after the
    // async Pixel (fbevents.js) has written _fbp/_fbc — see utils/adjust.ts.
    const buildLink = () => generateAdjustInviteQuery(null, { deeplink_path: '/', feature: 'main_page_download_button' });
    const linkQuery = { link: buildLink() };
    const getCopyLink = buildLink;
    useEffect(() => {
        new ClipboardJS(appstoreButton.current, { text: getCopyLink });
        new ClipboardJS(googleplayButton.current, { text: getCopyLink });
    }, [getCopyLink]);
    const settings = {
        dots: true,
        arrows: false,
        infinite: true,
        fade: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        cssEase: 'linear',
    };
    return (_jsxs(Wrapper, { children: [_jsx(FormattedMessage, Object.assign({}, messages.title, { children: (title) => _jsx(Title, { children: title }) })), _jsx(Subtitle, { children: _jsx(FormattedMessage, Object.assign({}, messages.subtitle)) }), _jsx(StyledSlider, Object.assign({}, settings, { children: slides.map((slide, id) => (_jsxs(Slide, { children: [_jsx(LogoWrap, { children: _jsx(Logo, { src: slide.logo, width: "207", height: "38", alt: slide.alt }) }), _jsx(Text, { children: slide.text })] }, id))) })), _jsxs(StoresBlock, { children: [_jsx(DownloadCtaTitle, { children: _jsx(FormattedMessage, Object.assign({}, messages.downloadCta)) }), _jsxs(Stores, { children: [_jsxs(Appstore, { onClick: handleAppstoreClick, ref: appstoreButton, href: linkQuery.link, children: [_jsx(AppstoreIcon, {}), _jsxs(StoreDescription, { children: [_jsx(StoreName, { children: _jsx(FormattedMessage, Object.assign({}, messages.appstoreName)) }), _jsxs(StoreRating, { children: [_jsx("strong", { children: "4.6" }), "\u00A0", _jsx(FormattedMessage, Object.assign({}, messages.appstoreRating))] })] })] }), _jsxs(GooglePlay, { onClick: handleGooglePlayClick, ref: googleplayButton, href: linkQuery.link, children: [_jsx(GoogleplayIcon, {}), _jsxs(StoreDescription, { children: [_jsx(StoreName, { children: _jsx(FormattedMessage, Object.assign({}, messages.googleplayName)) }), _jsxs(StoreRating, { children: [_jsx("strong", { children: "4.4" }), "\u00A0", _jsx(FormattedMessage, Object.assign({}, messages.googleplayRating))] })] })] })] })] })] }));
    function handleAppstoreClick(event) {
        event.preventDefault();
        analytics.track('QuotesAppstoreDownloadButton', 'click', pickBy({
            origin,
            feature: 'main_page_download_button',
        }, identity));
        window.open('https://itunes.apple.com/app/sweatcoin-app-that-pays-you/id971023427', '_blank');
    }
    function handleGooglePlayClick(event) {
        event.preventDefault();
        analytics.track('QuotesGooglePlayDownloadButton', 'click', pickBy({
            origin,
            feature: 'main_page_download_button',
        }, identity));
        window.open('https://play.google.com/store/apps/details?id=in.sweatco.app', '_blank');
    }
};
const Wrapper = styled.div `
  box-sizing: border-box;
  margin: 0 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 24px 0px 24px;
  background: linear-gradient(253deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 20px;

  @media ${desktop()} {
    width: 980px;
    margin: 120px auto 40px;
    padding: 32px 0px;
  }
`;
const Title = styled.h3 `
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  letter-spacing: 1px;
  line-height: 1.2;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 24px;

  @media ${desktop()} {
    font-size: 18px;
  }
`;
const Subtitle = styled.h3 `
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  letter-spacing: 1px;
  line-height: 1.2;
  text-align: center;
  margin-bottom: 48px;

  @media ${desktop()} {
    font-size: 16px;
  }
`;
const StyledSlider = styled(Slider) `
  position: relative;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;

  @media ${desktop()} {
    width: 820px;
    height: 130px;
    margin: 0 auto;
    padding: 0;
  }

  .slick-dots {
    display: flex !important;
    justify-content: center;
    padding: 0 !important;
    margin: 0 !important;

    li {
      list-style: none !important;
      margin: 0 4px;
      line-height: 0;

      button {
        width: 4px;
        height: 4px;
        background-color: #ffffff;
        opacity: 0.3;
        border-radius: 6px;
        border: none;
        font-size: 0;
        padding: 0;
        cursor: pointer;
        transition: all 0.3s linear;
      }

      &.slick-active {
        button {
          width: 32px;
          opacity: 1;
        }
      }
    }
  }
`;
const Slide = styled.div `
  padding-bottom: 16px;

  @media ${desktop()} {
    padding-bottom: 25px;
  }
`;
const LogoWrap = styled.div ``;
const Logo = styled.img `
  height: 38px;
  display: block;
  width: auto;
  margin: 0 auto 12px;
`;
const Text = styled.p `
  font-style: italic;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  font-size: 18px;
  letter-spacing: 1px;
  line-height: 1.2;
  text-transform: lowercase;
  text-align: center;
`;
const DownloadCtaTitle = styled.h3 `
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
`;
const StoresBlock = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 28px;
  margin-top: 28px;

  @media ${desktop()} {
    padding-top: 32px;
    margin-top: 32px;
    justify-content: center;
  }
`;
const Stores = styled.div `
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: start;
  margin-top: 32px;
  margin-bottom: 12px;

  @media ${desktop()} {
    flex-direction: row;
    justify-content: center;
  }
`;
const Button = styled.a `
  display: flex;
  margin: 0 32px;

  @media ${desktop()} {
    width: 266px;
    margin: 0 65px;
  }

  & + & {
    margin-top: 40px;

    @media ${desktop()} {
      margin-top: 0px;
    }
  }
`;
const Appstore = styled(Button) `
  margin-right: 8px;

  @media ${desktop()} {
    margin-right: 14px;
  }
`;
const GooglePlay = styled(Button) ``;
const StoreIcon = styled.div `
  width: 48px;
  height: 48px;
  margin-right: 24px;

  @media ${desktop()} {
    width: 58px;
    height: 58px;
  }
`;
const AppstoreIcon = styled(StoreIcon) `
  background: url(${appstoreSrc}) 50% 50% no-repeat;
  background-size: contain;
`;
const GoogleplayIcon = styled(StoreIcon) `
  background: url(${googleplaySrc}) 50% 50% no-repeat;
  background-size: contain;
`;
const StoreDescription = styled.div `
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`;
const StoreName = styled.div `
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  font-size: 14px;
  letter-spacing: 1px;
  line-height: 1;
  text-transform: uppercase;
  margin-bottom: 4px;

  @media ${desktop()} {
    line-height: 1.2;
    margin-bottom: 0;
  }
`;
const StoreRating = styled.div `
  display: flex;
  align-items: flex-end;

  @media ${desktop()} {
    margin-top: 2px;
  }

  strong {
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    color: #ffffff;
    font-size: 24px;
    letter-spacing: 1px;
    line-height: 1.2;
    text-transform: capitalize;

    @media ${desktop()} {
      font-size: 28px;
    }
  }

  span {
    font-family: 'Inter', sans-serif;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    letter-spacing: 1.4px;
    line-height: 1.2;
    text-transform: uppercase;
    font-weight: 400;
    position: relative;
    top: -3px;

    @media ${desktop()} {
      top: -8px;
    }
  }
`;
export default Quotes;
